import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import QBDealPicker, { type QBDealOption } from '../sales-activity/QBDealPicker';
import type { PurchaseRequisition } from '../../types/purchasing';
import { useDealPurchaseBudgetByNode } from '../../hooks/useDealPurchaseBudgetByNode';
import { groupBudgetEnvelope, type OpexBudgets } from '../../utils/opexBudgets';
import {
  listJobNodePurchases,
  listOpexGroupPurchases,
  summarizeOpexGroupMonth,
  type BudgetPurchaseLine,
} from '../../utils/purchaseBudget';
import { formatOpexBudgetedDisplay, formatPurchaseBudgetUsd } from '../../utils/purchaseBudgetDisplay';
import { PURCHASE_OVERHEAD_GROUPS } from '../../utils/purchasingSpendClass';
import { parseQBLinkTargetId } from '../../utils/qbDealLinkOptions';
import './PurchaseBudgetsModal.css';

type BudgetTab = 'opex' | 'project';

const PurchaseBudgetsModal: React.FC<{
  rows: PurchaseRequisition[];
  opexBudgets: OpexBudgets;
  dealOptions: QBDealOption[];
  showFinancesLink: boolean;
  onClose: () => void;
  onOpenPurchase?: (id: string) => void;
}> = ({ rows, opexBudgets, dealOptions, showFinancesLink, onClose, onOpenPurchase }) => {
  const [tab, setTab] = useState<BudgetTab>('opex');
  const [jobPicker, setJobPicker] = useState('');
  const [openIds, setOpenIds] = useState<Record<string, boolean>>({});

  const opexRows = useMemo(() => {
    const now = new Date();
    return PURCHASE_OVERHEAD_GROUPS.map(group => {
      const envelope = groupBudgetEnvelope(opexBudgets, group.id);
      const summary = summarizeOpexGroupMonth({
        rows,
        group: group.id,
        budgetedUsd: envelope.amountUsd,
        interval: envelope.interval,
        now,
      });
      const purchases = listOpexGroupPurchases({
        rows,
        group: group.id,
        interval: envelope.interval,
        now,
      });
      return { group, label: group.label, summary, purchases };
    });
  }, [rows, opexBudgets]);

  const periodLabel = opexRows[0]?.summary.periodLabel;

  return (
    <div className="fin-modal-overlay" onClick={onClose}>
      <div
        className="fin-modal po-modal po-budgets-modal"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-labelledby="po-budgets-title"
      >
        <div className="fin-modal-header">
          <h3 id="po-budgets-title">Budgets</h3>
          <button type="button" className="fin-modal-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="fin-modal-body">
          <div className="po-budgets-tabs">
            <button
              type="button"
              className={`fin-pill ${tab === 'opex' ? 'po-pill-active' : ''}`}
              onClick={() => setTab('opex')}
            >
              OpEx
            </button>
            <button
              type="button"
              className={`fin-pill ${tab === 'project' ? 'po-pill-active' : ''}`}
              onClick={() => setTab('project')}
            >
              Project
            </button>
          </div>

          {tab === 'opex' ? (
            <>
              {periodLabel ? (
                <p className="po-budgets-period">Purchase activity for {periodLabel} (Chicago)</p>
              ) : null}
              <div className="po-budgets-table-wrap">
                <table className="po-budgets-table">
                  <thead>
                    <tr>
                      <th>OpEx group</th>
                      <th>Budgeted</th>
                      <th>Reserved</th>
                      <th>Committed</th>
                      <th>Remaining</th>
                    </tr>
                  </thead>
                  <tbody>
                    {opexRows.map(row => {
                      const over =
                        row.summary.remainingUsd != null && row.summary.remainingUsd < -0.005;
                      return (
                        <BudgetGroupRows
                          key={row.group.id}
                          id={row.group.id}
                          label={row.label}
                          open={!!openIds[row.group.id]}
                          onToggle={() => toggleOpen(setOpenIds, row.group.id)}
                          over={over}
                          values={[
                            formatOpexBudgetedDisplay(row.summary),
                            formatPurchaseBudgetUsd(row.summary.reservedUsd),
                            formatPurchaseBudgetUsd(row.summary.committedUsd),
                            row.summary.remainingUsd == null
                              ? 'Not set'
                              : formatPurchaseBudgetUsd(row.summary.remainingUsd),
                          ]}
                          lines={row.purchases}
                          caption={purchaseCountCaption(row.purchases.length, `in ${row.summary.periodLabel}`)}
                          onOpenPurchase={onOpenPurchase}
                        />
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <>
              <div className="po-budgets-project-picker">
                <span className="fin-modal-label">Job / project</span>
                <QBDealPicker
                  value={jobPicker}
                  onChange={setJobPicker}
                  dealOptions={dealOptions}
                  emptyLabel="Choose a project"
                  aria-label="Project for budget snapshot"
                />
              </div>
              <ProjectBudgetTable
                jobPicker={jobPicker}
                rows={rows}
                onOpenPurchase={onOpenPurchase}
              />
            </>
          )}

          <div className="po-budgets-footer">
            {showFinancesLink ? (
              <Link to="/finances/overhead" onClick={onClose}>
                Edit OpEx budgets in Finances → Overhead planning
              </Link>
            ) : (
              <span>OpEx budget envelopes are set in Finances → Overhead planning.</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

function toggleOpen(
  setOpenIds: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
  id: string,
) {
  setOpenIds(prev => ({ ...prev, [id]: !prev[id] }));
}

function purchaseCountCaption(count: number, where: string): string {
  if (count === 0) return `No purchases ${where}`;
  const noun = count === 1 ? 'purchase' : 'purchases';
  return `${count} ${noun} ${where}`;
}

function formatBudgetPurchaseDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    timeZone: 'America/Chicago',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function BudgetGroupRows({
  id,
  label,
  open,
  onToggle,
  over,
  values,
  lines,
  caption,
  onOpenPurchase,
}: {
  id: string;
  label: string;
  open: boolean;
  onToggle: () => void;
  over: boolean;
  values: string[];
  lines: BudgetPurchaseLine[];
  caption: string;
  onOpenPurchase?: (id: string) => void;
}) {
  const detailId = `po-budgets-detail-${id}`;
  return (
    <>
      <tr
        className={[
          'po-budgets-row--toggle',
          over ? 'po-budgets-row--over' : '',
          open ? 'po-budgets-row--open' : '',
        ].filter(Boolean).join(' ')}
        onClick={onToggle}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggle();
          }
        }}
        tabIndex={0}
        role="button"
        aria-expanded={open}
        aria-controls={detailId}
      >
        <td>
          <span className="po-budgets-expand">
            {open ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
            <span>{label}</span>
            {lines.length > 0 ? <span className="po-budgets-count">{lines.length}</span> : null}
          </span>
        </td>
        {values.map((value, index) => (
          <td key={`${id}-${index}`}>{value}</td>
        ))}
      </tr>
      {open ? (
        <tr id={detailId} className={over ? 'po-budgets-detail po-budgets-detail--over' : 'po-budgets-detail'}>
          <td colSpan={values.length + 1}>
            <p className="po-budgets-detail-caption">{caption}</p>
            {lines.length > 0 ? (
              <ul className="po-budgets-pos">
                {lines.map(line => {
                  const meta = [line.vendorName, line.title].filter(Boolean).join(' · ');
                  const body = (
                    <>
                      <span className="po-budgets-po-number">{line.number}</span>
                      <span className="po-budgets-po-meta">{meta}</span>
                      <span className={`po-budgets-kind po-budgets-kind--${line.statusId}`}>{line.statusLabel}</span>
                      <span className="po-budgets-po-date">{formatBudgetPurchaseDate(line.at)}</span>
                      <span className="po-budgets-po-amount">
                        {formatPurchaseBudgetUsd(line.amountUsd)}
                        {line.poAmountUsd != null ? (
                          <span className="po-budgets-po-sub">PO {formatPurchaseBudgetUsd(line.poAmountUsd)}</span>
                        ) : null}
                      </span>
                    </>
                  );
                  return (
                    <li key={line.id}>
                      {onOpenPurchase ? (
                        <button
                          type="button"
                          className="po-budgets-po"
                          onClick={event => {
                            event.stopPropagation();
                            onOpenPurchase(line.id);
                          }}
                        >
                          {body}
                        </button>
                      ) : (
                        <div className="po-budgets-po">{body}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </td>
        </tr>
      ) : null}
    </>
  );
}

function ProjectBudgetTable({
  jobPicker,
  rows,
  onOpenPurchase,
}: {
  jobPicker: string;
  rows: PurchaseRequisition[];
  onOpenPurchase?: (id: string) => void;
}) {
  const dealId = parseQBLinkTargetId(jobPicker).dealId;
  const { loaded, nodes } = useDealPurchaseBudgetByNode(dealId || undefined, rows);
  const [openIds, setOpenIds] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setOpenIds({});
  }, [dealId]);

  const purchasesByBucket = useMemo(() => {
    const byBucket: Record<string, BudgetPurchaseLine[]> = {};
    if (!dealId) return byBucket;
    for (const node of nodes) {
      byBucket[node.bucket] = listJobNodePurchases({
        rows,
        dealId,
        plBucket: node.bucket,
      });
    }
    return byBucket;
  }, [dealId, nodes, rows]);

  if (!jobPicker || !dealId) {
    return <p className="po-budgets-empty">Select a project to see proposal node budgets.</p>;
  }

  if (!loaded) {
    return <p className="po-budgets-loading">Loading proposal and QuickBooks actuals…</p>;
  }

  if (nodes.length === 0) {
    return (
      <p className="po-budgets-empty">
        No proposal node budgets or purchase activity for this project yet.
      </p>
    );
  }

  return (
    <div className="po-budgets-table-wrap">
      <table className="po-budgets-table">
        <thead>
          <tr>
            <th>Node</th>
            <th>Proposal budget</th>
            <th>QB actual</th>
            <th>Reserved</th>
            <th>Committed</th>
            <th>Remaining</th>
          </tr>
        </thead>
        <tbody>
          {nodes.map(row => {
            const over = row.summary.remainingUsd < -0.005;
            const purchases = purchasesByBucket[row.bucket] || [];
            return (
              <BudgetGroupRows
                key={row.bucket}
                id={row.bucket}
                label={row.label}
                open={!!openIds[row.bucket]}
                onToggle={() => toggleOpen(setOpenIds, row.bucket)}
                over={over}
                values={[
                  formatPurchaseBudgetUsd(row.budgetUsd),
                  formatPurchaseBudgetUsd(row.summary.actualUsd),
                  formatPurchaseBudgetUsd(row.summary.reservedUsd),
                  formatPurchaseBudgetUsd(row.summary.committedUsd),
                  formatPurchaseBudgetUsd(row.summary.remainingUsd),
                ]}
                lines={purchases}
                caption={
                  purchases.length
                    ? purchaseCountCaption(purchases.length, 'on this node')
                    : row.summary.actualUsd > 0.005
                      ? 'No purchase orders. QuickBooks actual is on the row above.'
                      : 'No purchase orders on this node'
                }
                onOpenPurchase={onOpenPurchase}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default PurchaseBudgetsModal;
